import React, { useEffect, useRef } from "react";
import styled from "styled-components";

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: -1;
  pointer-events: none;
  background: var(--bg-color);
  transition: background 0.3s ease;
`;

const InteractiveBackground = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;
    let dots = [];
    const spacing = 35;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initGrid();
    };

    const initGrid = () => {
      dots = [];
      const cols = Math.ceil(canvas.width / spacing) + 1;
      const rows = Math.ceil(canvas.height / spacing) + 1;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * spacing;
          const y = r * spacing;
          dots.push({
            x,
            y,
            px: x,
            py: y,
            size: 2.0
          });
        }
      }
    };

    const handleMouseMove = (e) => {
      mouseRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);

    resizeCanvas();

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Fetch active text color to use for the dots
      const themeColor = getComputedStyle(document.body).getPropertyValue("--text-color").trim() || "#000000";
      ctx.fillStyle = themeColor;

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const radius = 220; // Interaction threshold (larger)

      for (let i = 0; i < dots.length; i++) {
        const dot = dots[i];
        const dx = dot.x - mx;
        const dy = dot.y - my;
        const distSq = dx * dx + dy * dy;
        const dist = Math.sqrt(distSq);

        let tx = dot.x;
        let ty = dot.y;
        let targetSize = 2.0;
        let targetOpacity = 0.22; // Slightly more visible baseline

        if (dist < radius) {
          const force = (radius - dist) / radius; // 0 to 1
          const angle = Math.atan2(dy, dx);
          
          // Push dots away from the cursor (much stronger push)
          const disp = force * 35;
          tx = dot.x + Math.cos(angle) * disp;
          ty = dot.y + Math.sin(angle) * disp;

          // Shrink and dim dots under the cursor dramatically to leave a sharp impression
          targetSize = 2.0 - force * 1.5;
          targetOpacity = 0.22 - force * 0.18;
        }

        // LERP / Ease position transitions
        dot.px += (tx - dot.px) * 0.1;
        dot.py += (ty - dot.py) * 0.1;

        // LERP size transitions
        dot.size += (targetSize - dot.size) * 0.1;

        ctx.beginPath();
        ctx.arc(dot.px, dot.py, dot.size, 0, Math.PI * 2);
        
        ctx.save();
        ctx.globalAlpha = targetOpacity;
        ctx.fill();
        ctx.restore();
      }

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <Canvas ref={canvasRef} />;
};

export default InteractiveBackground;
